import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { HiEye, HiX } from "react-icons/hi";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import Search from "components/common/Search/Search";
import PaginationComponent from "components/common/Pagination/index";
import TableHeader from "components/common/Table/TableHeader";
import AvatarGroup from "components/common/Avatar/AvatarGroup";
import { classNames, capitalizeWords } from "Utils/utils";
import { getShiftRequestsState } from "store/selector/rostering.selector";
import { fetchShiftRequests } from "store/actions/rostering.actions";

const SHIFT_REQUEST_LIST_LIMIT = 10;
const SKELETON_ROW_COUNT = 6;

const TABS = [
  { name: "Pending", value: "pending" },
  { name: "Approved", value: "approved" },
  { name: "Rejected", value: "rejected" },
  { name: "In Review", value: "in_review" },
];

const CELL_CLASS =
  "px-2 py-3 align-middle text-xs leading-snug sm:px-3 sm:py-3.5 sm:text-sm lg:px-4";
const HEADER_CLASS = "px-2 sm:px-3 lg:px-4";
const TABLE_HEADER_CELL_CLASS = classNames(
  HEADER_CLASS,
  "align-middle whitespace-normal break-words bg-gray-50 !py-2 text-xs leading-tight sm:!py-2.5 sm:text-sm"
);
const TABLE_HEAD_CLASS = "sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_0_#e5e7eb]";
const MODAL_PANEL_CLASS =
  "flex h-[85vh] w-[min(100%,96vw)] min-h-0 min-w-0 max-h-[85vh] flex-col overflow-hidden lg:w-[min(100%,90vw)] xl:w-[min(72rem,82vw)] 2xl:w-[min(72rem,70vw)]";
const TOOLBAR_CLASS =
  "flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 pb-2 pt-2 sm:gap-3 sm:px-4 sm:pb-2 sm:pt-3";
const TABLE_SECTION_CLASS =
  "flex min-h-0 flex-1 basis-0 flex-col overflow-hidden bg-white";
const TABLE_TEXT_CLASS = "text-xs leading-snug sm:text-sm";
const MODAL_BODY_LAYOUT_CLASS =
  "flex h-full min-h-0 flex-col overflow-hidden";
const TABLE_SCROLL_CLASS =
  "custom-scroll min-h-0 flex-1 basis-0 overflow-x-hidden overflow-y-auto";

const Pulse = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-[#E5E7EB] ${className}`} />
);

const ShiftRequestsTableColGroup = ({ showActions }) => (
  <colgroup>
    <col className="w-[18%] lg:w-[20%]" />
    <col className="w-[17%] lg:w-[18%]" />
    <col className="w-[20%] lg:w-[21%]" />
    <col />
    {showActions ? <col className="w-[72px] lg:w-[84px]" /> : null}
  </colgroup>
);

/** Matches Shift Requests table layout while list is fetching. */
const ShiftRequestsTableSkeleton = ({ showActions = true }) => (
  <div className={MODAL_BODY_LAYOUT_CLASS}>
    <div className={TOOLBAR_CLASS}>
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Pulse key={i} className="h-7 w-24 rounded-full" />
        ))}
      </div>
      <Pulse className="h-9 w-64 rounded-lg" />
    </div>

    <div className={TABLE_SECTION_CLASS}>
      <div className={TABLE_SCROLL_CLASS}>
        <table
          className={classNames(
            "w-full table-fixed divide-y divide-gray-200",
            TABLE_TEXT_CLASS
          )}
        >
          <ShiftRequestsTableColGroup showActions={showActions} />
          <thead className={TABLE_HEAD_CLASS}>
            <tr>
              <th className="px-2 py-2 text-left sm:px-3 lg:px-4">
                <Pulse className="h-3 w-20" />
              </th>
              <th className="px-4 py-3 text-left">
                <Pulse className="h-3 w-24" />
              </th>
              <th className="px-4 py-3 text-left">
                <Pulse className="h-3 w-24" />
              </th>
              <th className="px-4 py-3 text-left">
                <Pulse className="h-3 w-28" />
              </th>
              {showActions ? (
                <th className="px-4 py-3">
                  <div className="flex justify-center">
                    <Pulse className="h-3 w-14" />
                  </div>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, row) => (
              <tr key={row}>
                <td className={CELL_CLASS}>
                  <div className="flex items-center gap-2">
                    <Pulse className="h-8 w-8 shrink-0 rounded-full" />
                    <Pulse className="h-3.5 w-32" />
                  </div>
                </td>
                <td className={CELL_CLASS}>
                  <div className="space-y-2">
                    <Pulse className="h-3.5 w-28" />
                    <Pulse className="h-3 w-36" />
                  </div>
                </td>
                <td className={CELL_CLASS}>
                  <Pulse className="h-3.5 w-28" />
                </td>
                <td className={CELL_CLASS}>
                  <Pulse className="h-6 w-36 rounded-full" />
                </td>
                {showActions ? (
                  <td className={CELL_CLASS}>
                    <div className="flex items-center justify-center gap-x-3">
                      <Pulse className="h-7 w-7 rounded-md" />
                      <Pulse className="h-7 w-7 rounded-md" />
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-white px-4 py-3">
        <Pulse className="h-3 w-28" />
        <div className="flex gap-2">
          <Pulse className="h-8 w-8 rounded-md" />
          <Pulse className="h-8 w-8 rounded-md" />
          <Pulse className="h-8 w-8 rounded-md" />
        </div>
      </div>
    </div>
  </div>
);

const SERVICE_OUTCOME_PILL_STYLES = [
  "bg-orange-100 text-orange-800",
  "bg-amber-100 text-amber-800",
  "bg-green-100 text-green-800",
  "bg-blue-100 text-blue-800",
  "bg-purple-100 text-purple-800",
  "bg-pink-100 text-pink-800",
  "bg-teal-100 text-teal-800",
  "bg-indigo-100 text-indigo-800",
];

const getAvatarColor = (name = "") => {
  const colors = [
    "bg-blue-500",
    "bg-purple-500",
    "bg-green-500",
    "bg-orange-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-red-500",
    "bg-teal-500",
  ];
  return colors[name.charCodeAt(0) % colors.length];
};

const getInitials = (firstName = "", lastName = "") => {
  const first = (firstName?.[0] || "").toUpperCase();
  const last = (lastName?.[0] || "").toUpperCase();
  return `${first}${last}` || "?";
};

const getServiceCategoryPillClass = (categoryId) => {
  const idx = categoryId
    ? Math.abs(Number(categoryId)) % SERVICE_OUTCOME_PILL_STYLES.length
    : 0;
  return SERVICE_OUTCOME_PILL_STYLES[idx];
};

const getDayLabel = (reqDate) => {
  if (!reqDate) {
    return null;
  }
  const date = moment(reqDate);
  if (date.isSame(moment().add(1, "day"), "day")) {
    return { label: "Tomorrow", className: "text-orange-500" };
  }
  return { label: date.format("dddd"), className: "text-gray-500" };
};

const formatDateDisplay = (reqDate) =>
  reqDate ? moment(reqDate).format("DD/MM/YY") : "—";

const formatTimeRange = (startTime, endTime) => {
  const start = startTime
    ? moment(startTime, ["HH:mm:ss", "HH:mm"]).format("h:mm A")
    : "—";
  const end = endTime
    ? moment(endTime, ["HH:mm:ss", "HH:mm"]).format("h:mm A")
    : "—";
  return `${start} - ${end}`;
};

const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return "";
  }
  const start = moment(startTime, ["HH:mm:ss", "HH:mm"]);
  let end = moment(endTime, ["HH:mm:ss", "HH:mm"]);
  if (end.isBefore(start)) {
    end.add(1, "day");
  }
  const hours =
    Math.round(moment.duration(end.diff(start)).asHours() * 10) / 10;
  return `(${hours}h)`;
};

const formatParticipantNames = (participants) => {
  if (!participants?.length) {
    return "—";
  }
  return participants
    .map((p) => `${p?.first_name || ""} ${p?.last_name || ""}`.trim())
    .filter(Boolean)
    .join(", ");
};

const getRequestedByRoleLabel = (userType) => {
  const type = String(userType || "").toLowerCase();
  if (type === "family") {
    return "Guardian";
  }
  if (type === "participant") {
    return "Participant";
  }
  return null;
};

const formatRequestedByName = (requestedBy) => {
  const userType = String(requestedBy?.user_type || "").toLowerCase();

  if (userType === "participant") {
    const pt = requestedBy?.participant;
    const participantName = pt
      ? `${pt.first_name || ""} ${pt.last_name || ""}`.trim()
      : "";
    if (participantName) {
      return capitalizeWords(participantName);
    }
  }

  const name = (requestedBy?.name || "").trim();
  if (name) {
    return userType === "family" ? name : capitalizeWords(name);
  }
  return "—";
};

const ShiftRequestsModal = ({
  isOpen,
  onClose,
  isUserGracePeriodOver,
  serviceTypeOptions = [],
  onView,
  onReject,
}) => {
  const dispatch = useDispatch();
  const { list: shiftRequests, loading } = useSelector(getShiftRequestsState);
  const updatingStatus = useSelector(
    (state) =>
      state.rostering?.getIn?.(["shiftRequests", "updatingStatus"]) || false
  );

  const [activeTab, setActiveTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchShiftRequests());
      setActiveTab("pending");
      setSearch("");
      setPage(1);
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, search]);

  const formatServiceCategory = (request) => {
    if (request?.invoice_service_type?.name) {
      return request.invoice_service_type.name;
    }
    const categoryId = request?.service_category_id;
    if (!categoryId) {
      return "—";
    }
    const match = serviceTypeOptions.find((opt) => opt.value === categoryId);
    return match?.label || "—";
  };

  const statusCounts = useMemo(() => {
    const counts = { pending: 0, approved: 0, rejected: 0, in_review: 0 };
    shiftRequests.forEach((request) => {
      const status = String(request?.status || "").toLowerCase();
      if (status in counts) {
        counts[status] += 1;
      }
    });
    return counts;
  }, [shiftRequests]);

  const filteredRequests = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    return shiftRequests.filter((request) => {
      const status = String(request?.status || "").toLowerCase();
      if (status !== activeTab) {
        return false;
      }
      if (searchTerm) {
        const names = formatParticipantNames(request?.participants).toLowerCase();
        if (!names.includes(searchTerm)) {
          return false;
        }
      }
      return true;
    });
  }, [shiftRequests, activeTab, search]);

  const totalPages =
    Math.ceil(filteredRequests.length / SHIFT_REQUEST_LIST_LIMIT) || 1;

  const paginatedRequests = useMemo(() => {
    const start = (page - 1) * SHIFT_REQUEST_LIST_LIMIT;
    return filteredRequests.slice(start, start + SHIFT_REQUEST_LIST_LIMIT);
  }, [filteredRequests, page]);

  const isActionTab =
    activeTab === "pending" || activeTab === "in_review";
  const colSpan = isActionTab ? 5 : 4;
  const emptyTabLabel =
    TABS.find((tab) => tab.value === activeTab)?.name?.toLowerCase() || activeTab;

  const renderParticipantCell = (participants) => {
    if (!participants?.length) {
      return <span className="text-gray-400 text-sm">-</span>;
    }

    if (participants.length === 1) {
      const participant = participants[0];
      const participantName = capitalizeWords(
        `${participant?.first_name || ""} ${participant?.last_name || ""}`.trim()
      );
      return (
        <div className="flex min-w-0 items-center gap-2">
          <div
            className={classNames(
              "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white",
              getAvatarColor(participantName)
            )}
          >
            {getInitials(participant?.first_name, participant?.last_name)}
          </div>
          <span
            title={participantName || "N/A"}
            className="min-w-0 break-words text-gray-900 line-clamp-2"
          >
            {participantName || "N/A"}
          </span>
        </div>
      );
    }

    return (
      <AvatarGroup
        arrayData={participants.map((p) => ({
          ...p,
          first_name: p.first_name || "",
          last_name: p.last_name || "",
          profile_picture: p.profile_picture,
        }))}
        cssClass="mt-0"
        maxLength={3}
        isNoRing={true}
      />
    );
  };

  return (
    <ModalSkeleton
      title="Shift Requests"
      isOpen={isOpen}
      cssClass={MODAL_PANEL_CLASS}
      bodyPadding="!p-0"
      stretchBody
      closeModal={onClose}
    >
      {loading ? (
        <ShiftRequestsTableSkeleton showActions={isActionTab} />
      ) : (
        <div className={MODAL_BODY_LAYOUT_CLASS}>
          <div className={TOOLBAR_CLASS}>
            <nav className="flex min-w-0 flex-1 flex-wrap gap-2" aria-label="Shift request tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setActiveTab(tab.value)}
                  className={classNames(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    activeTab === tab.value
                      ? "border-[#293f6a] bg-[#293f6a] text-white"
                      : "border-gray-300 bg-white text-gray-500 hover:border-gray-400"
                  )}
                >
                  {tab.name} ({statusCounts[tab.value] ?? 0})
                </button>
              ))}
            </nav>
            <div className="w-full min-w-0 sm:w-auto sm:max-w-xs md:max-w-sm lg:max-w-[16rem]">
              <Search
                search={search}
                setSearch={setSearch}
                placeholder="Search by participant name"
                width="w-full"
              />
            </div>
          </div>

          <div className={TABLE_SECTION_CLASS}>
            <div className={TABLE_SCROLL_CLASS}>
              <table
                className={classNames(
                  "w-full table-fixed divide-y divide-gray-200",
                  TABLE_TEXT_CLASS
                )}
              >
                <ShiftRequestsTableColGroup showActions={isActionTab} />
                <thead className={TABLE_HEAD_CLASS}>
                  <tr>
                    <TableHeader
                      isSort={false}
                      label="Participant"
                      cssClass={TABLE_HEADER_CELL_CLASS}
                    />
                    <TableHeader
                      isSort={false}
                      label="Date & Time"
                      cssClass={TABLE_HEADER_CELL_CLASS}
                    />
                    <TableHeader
                      isSort={false}
                      label="Requested By"
                      cssClass={TABLE_HEADER_CELL_CLASS}
                    />
                    <TableHeader
                      isSort={false}
                      label="Service Category"
                      cssClass={TABLE_HEADER_CELL_CLASS}
                    />
                    {isActionTab && (
                      <TableHeader
                        isSort={false}
                        label="Actions"
                        cssClass={classNames(
                          TABLE_HEADER_CELL_CLASS,
                          "text-center"
                        )}
                        innerCssClass="justify-center"
                      />
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredRequests.length === 0 ? (
                    <tr>
                      <td
                        colSpan={colSpan}
                        className="px-6 py-8 text-center text-gray-500"
                      >
                        No {emptyTabLabel} shift requests
                      </td>
                    </tr>
                  ) : (
                    paginatedRequests.map((request) => {
                      const dayLabel = getDayLabel(request?.req_date);
                      const serviceCategoryId =
                        request?.service_category_id ??
                        request?.invoice_service_type?.id;

                      return (
                        <tr
                          key={request?.uuid || request?.id}
                          className="hover:bg-gray-50"
                        >
                          <td
                            className={classNames(
                              CELL_CLASS,
                              "min-w-0 text-sm text-gray-900"
                            )}
                          >
                            {renderParticipantCell(request?.participants)}
                          </td>
                          <td
                            className={classNames(
                              CELL_CLASS,
                              "text-sm text-gray-500"
                            )}
                          >
                            <div className="break-words leading-snug">
                              <div className="font-semibold text-gray-900">
                                {formatDateDisplay(request?.req_date)}
                                {dayLabel && (
                                  <span
                                    className={classNames(
                                      "ml-1 block font-normal sm:ml-1.5 sm:inline",
                                      dayLabel.className
                                    )}
                                  >
                                    {dayLabel.label}
                                  </span>
                                )}
                              </div>
                              <div className="break-words text-gray-500">
                                {formatTimeRange(
                                  request?.start_time,
                                  request?.end_time
                                )}
                                {formatDuration(
                                  request?.start_time,
                                  request?.end_time
                                )}
                              </div>
                            </div>
                          </td>
                          <td
                            className={classNames(
                              CELL_CLASS,
                              "min-w-0 text-sm text-gray-900"
                            )}
                          >
                            <div
                              title={formatRequestedByName(request?.requested_by)}
                              className="break-all font-medium leading-snug sm:break-words"
                            >
                              {formatRequestedByName(request?.requested_by)}
                            </div>
                            {getRequestedByRoleLabel(
                              request?.requested_by?.user_type
                            ) && (
                              <div className="break-words text-gray-500">
                                {getRequestedByRoleLabel(
                                  request?.requested_by?.user_type
                                )}
                              </div>
                            )}
                          </td>
                          <td className={classNames(CELL_CLASS, "min-w-0")}>
                            <span
                              title={formatServiceCategory(request)}
                              className={classNames(
                                "inline-block max-w-full whitespace-normal break-words rounded-full px-2 py-1 text-left text-xs font-medium leading-snug",
                                getServiceCategoryPillClass(serviceCategoryId)
                              )}
                            >
                              {formatServiceCategory(request)}
                            </span>
                          </td>
                          {isActionTab && (
                            <td className={classNames(CELL_CLASS, "whitespace-nowrap")}>
                              <div className="flex items-center justify-center gap-x-1.5 sm:gap-x-2 lg:gap-x-3">
                                <div className="flex h-7 w-7 items-center justify-center">
                                  <button
                                    type="button"
                                    title="View"
                                    disabled={isUserGracePeriodOver}
                                    onClick={() => onView(request)}
                                  >
                                    <div
                                      className={classNames(
                                        "icon-eye-wrapper",
                                        isUserGracePeriodOver &&
                                          "cursor-not-allowed opacity-40"
                                      )}
                                    >
                                      <HiEye className="icon-size" />
                                    </div>
                                  </button>
                                </div>
                                <div className="flex h-7 w-7 items-center justify-center">
                                  <button
                                    type="button"
                                    title="Reject"
                                    disabled={
                                      isUserGracePeriodOver || updatingStatus
                                    }
                                    onClick={() => onReject(request)}
                                  >
                                    <div
                                      className={classNames(
                                        "icon-x-wrapper",
                                        (isUserGracePeriodOver ||
                                          updatingStatus) &&
                                          "cursor-not-allowed opacity-40"
                                      )}
                                    >
                                      <HiX className="icon-size" />
                                    </div>
                                  </button>
                                </div>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {!loading && paginatedRequests.length > 0 && totalPages > 0 && (
              <div className="shrink-0 border-t border-gray-200 bg-white px-3 py-2 text-xs sm:px-4 sm:py-3 sm:text-sm">
                <PaginationComponent
                  page={page}
                  activeListLength={paginatedRequests}
                  limit={SHIFT_REQUEST_LIST_LIMIT}
                  totalList={filteredRequests.length}
                  totalPages={totalPages}
                  handlePagination={(newPage) => {
                    if (loading) {
                      return;
                    }
                    setPage(newPage);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </ModalSkeleton>
  );
};

export default ShiftRequestsModal;
